import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { BookText, History, PhoneCall, Settings, Users } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Softphone } from "@/components/Softphone";
import { Dot } from "@/components/StatusBits";
import { api, errMsg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { to: "/", label: "Dialer", icon: PhoneCall, end: true },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/scripts", label: "Scripts & AI", icon: BookText },
  { to: "/history", label: "Call history", icon: History },
  { to: "/settings", label: "Settings", icon: Settings },
];

const Pill = ({ name, ok, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}>
    <Dot on={ok} /> {name}: {ok ? "Configured" : "Not configured"}
  </span>
);

const Availability = () => {
  const { me, setMe } = useApp();
  const change = async (v) => {
    try {
      const { data } = await api.put("/me", { availability: v });
      setMe(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Select value={me?.availability || "offline"} onValueChange={change}>
      <SelectTrigger data-testid="availability-select" className="h-8 w-[130px] text-xs bg-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="available" data-testid="availability-available">Available</SelectItem>
        <SelectItem value="busy" data-testid="availability-busy">Busy</SelectItem>
        <SelectItem value="offline" data-testid="availability-offline">Offline</SelectItem>
      </SelectContent>
    </Select>
  );
};

export const Layout = ({ children }) => {
  const { status, me } = useApp();
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:flex w-56 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-200">
          <div className="h-7 w-7 rounded-md bg-zinc-900 text-white grid place-items-center"><PhoneCall className="h-4 w-4" /></div>
          <span className="font-heading font-bold tracking-tight text-slate-900" data-testid="app-brand">Phone Blower</span>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-zinc-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200 text-xs text-slate-500" data-testid="sidebar-user">
          {me?.name} · <span className="capitalize">{me?.role}</span>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 shrink-0 flex items-center gap-3 border-b border-slate-200 bg-white px-4">
          <nav className="flex md:hidden gap-1 overflow-x-auto">
            {NAV.map(({ to, icon: Icon, end, label }) => (
              <NavLink key={to} to={to} end={end} data-testid={`mnav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-zinc-900 text-white" : "text-slate-600"}`}>
                <Icon className="h-4 w-4" />
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-1.5">
            <Pill name="Twilio" ok={status?.twilio?.configured} testId="status-twilio" />
            <Pill name="ElevenLabs" ok={status?.elevenlabs?.configured} testId="status-elevenlabs" />
            <Pill name="AI" ok={status?.ai?.configured} testId="status-ai" />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Softphone />
            <Availability />
            <UserButton />
          </div>
        </header>
        <main className="flex-1 min-h-0 min-w-0 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
};
